<!-- src/components/admin/cards/AssignCardDialog.vue -->
<template>
  <v-card class="rounded-xl">
    <v-sheet color="primary" dark class="pa-4 d-flex align-center">
      <v-icon large class="mr-3">mdi-account-plus</v-icon>
      <div class="flex-grow-1">
        <div class="text-h6 font-weight-bold">{{ $t('Assigner une carte') }}</div>
        <div class="text-caption">{{ $t('Code') }}: {{ formatCode(card.code) }}</div>
      </div>
      <v-btn icon @click="$emit('cancel')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-sheet>
    
    <v-card-text class="pt-6">
      <v-form ref="form" @submit.prevent="assign">
        <!-- Recherche utilisateur -->
        <v-autocomplete
          v-model="selectedUser"
          :items="users"
          :loading="searchingUsers"
          :search-input.sync="userSearch"
          item-text="fullname"
          item-value="id"
          :label="$t('Rechercher un utilisateur') + ' *'"
          prepend-inner-icon="mdi-account-search"
          outlined
          dense
          clearable
          :no-data-text="$t('Aucun utilisateur trouvé')"
          :rules="[v => !!v || $t('Champ requis')]"
          return-object
        >
          <template v-slot:item="{ item }">
            <v-list-item-avatar color="primary" size="32">
              <span class="white--text text-caption">
                {{ getUserInitials(item) }}
              </span>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="text-capitalize">
                {{ item.fullname || item.username }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ item.email || item.phone_number || '-' }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
        
        <!-- Utilisateur sélectionné -->
        <v-card v-if="selectedUser" outlined class="mb-4">
          <v-card-text class="pa-3">
            <div class="d-flex align-center">
              <v-avatar color="primary" size="48" class="mr-3">
                <span class="white--text text-h6">
                  {{ getUserInitials(selectedUser) }}
                </span>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="font-weight-bold text-capitalize">
                  {{ selectedUser.fullname || selectedUser.username }}
                </div>
                <div class="text-caption grey--text">
                  {{ selectedUser.email || '-' }}
                </div>
                <div class="text-caption grey--text">
                  {{ selectedUser.phone_number || '-' }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
        
        <!-- Avertissement -->
        <v-alert
          v-if="selectedUser && selectedUser.has_active_card"
          type="warning"
          text
          dense
          border="left"
        >
          <div class="d-flex align-center">
            <v-icon small class="mr-2">mdi-alert</v-icon>
            <div>
              <div class="font-weight-medium">{{ $t('Attention') }}</div>
              <div class="text-caption">
                {{ $t('Cet utilisateur possède déjà une carte active. Elle sera automatiquement désactivée.') }}
              </div>
            </div>
          </div>
        </v-alert>
      </v-form>
    </v-card-text>
    
    <v-divider />
    
    <v-card-actions class="pa-4 justify-end">
      <v-btn text @click="$emit('cancel')" :disabled="assigning">
        <v-icon left>mdi-close</v-icon>
        {{ $t('commons.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        @click="assign"
        :loading="assigning"
        :disabled="!selectedUser"
      >
        <v-icon left>mdi-check</v-icon>
        {{ $t('Assigner') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import PhysicalCardService from "@/services/PhysicalCardService"
import AdminServices from "@/services/AdminServices"
import debounce from "lodash/debounce"

export default {
  name: "AssignCardDialog",
  
  props: {
    card: {
      type: Object,
      required: true
    }
  },
  
  data() {
    return {
      selectedUser: null,
      users: [],
      userSearch: "",
      searchingUsers: false,
      assigning: false,
    }
  },
  
  watch: {
    userSearch(val) {
      if (val && val.length >= 2) {
        this.debouncedSearchUsers(val)
      }
    }
  },
  
  created() {
    this.debouncedSearchUsers = debounce(this.searchUsers, 300)
  },
  
  methods: {
    formatCode(code) {
      return code ? code.substring(0, 8).toUpperCase() : '-'
    },
    
    getUserInitials(user) {
      if (!user) return 'U';
      const name = user.fullname || user.username || 'U';
      const parts = name.split(' ').filter(Boolean);
      if (parts.length === 0) return 'U';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    
    async searchUsers(search) {
      this.searchingUsers = true
      
      try {
        const data = await AdminServices.getUsers(this, { search, page_size: 20 })
        this.users = Array.isArray(data) ? data : (data.results || [])
      } catch (e) {
        console.error('Erreur recherche users:', e)
      } finally {
        this.searchingUsers = false
      }
    },
    
    async assign() {
      if (!this.$refs.form.validate()) return
      if (!this.selectedUser) return
      
      this.assigning = true
      
      try {
        await PhysicalCardService.assignCard(this, this.card.id, this.selectedUser.id)
        this.$emit('assigned')
      } catch (e) {
        const msg = e?.response?.data?.detail || this.$t("messages.error.operation_failed")
        this.$store.commit("app/message", { msg, color: "error" })
      } finally {
        this.assigning = false
      }
    },
  },
}
</script>